import { useEffect, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, Lock, ArrowRight, Loader2, ShieldCheck, KeyRound } from 'lucide-react';
import { authService } from '../../services/auth.service.js';
import { useAuthStore } from '../../stores/authStore.js';
import { OAuthButtons } from '../../components/auth/OAuthButtons.js';

const SAFE_REDIRECT_DEFAULT = '/dashboard';

/**
 * Resolve a safe post-login destination from the `redirect` query param.
 *
 * Only same-origin relative paths are allowed; anything that could resolve to
 * an external origin falls back to {@link SAFE_REDIRECT_DEFAULT}. The check
 * mirrors how a browser normalizes a URL before navigating, so the following
 * open-redirect vectors are all rejected:
 *  - protocol-relative (`//evil.com`)
 *  - absolute external (`https://evil.com`, `http://...`)
 *  - other schemes (`javascript:`, `data:`)
 *  - backslash / mixed-slash bypasses (`\evil.com`, `/\evil.com`) — browsers
 *    treat `\` as `/`, so `/\evil.com` would resolve to `//evil.com`
 *  - leading whitespace / control characters that browsers strip before
 *    normalizing (`\t//evil.com`, ` https://evil.com`)
 *  - non-slash relative inputs (`evil.com`)
 *
 * Accepted: same-origin relative paths starting with a single `/` and not
 * resolving to a `//`-prefixed (protocol-relative) path, e.g. `/dashboard`,
 * `/oauth/consent?consent_id=...`, `/users?page=2#frag`.
 */
function safeRedirect(raw: string | null): string {
  if (!raw) return SAFE_REDIRECT_DEFAULT;

  // Browsers strip leading C0 control chars (incl. tab/newline) and spaces
  // from a URL before resolving it; replicate that so a control-char prefix
  // can't smuggle a protocol-relative/absolute target past the slash checks.
  const trimmed = raw.replace(/^[\x00-\x20]+/, '');

  // Browsers normalize backslashes to forward slashes; do the same so that
  // `/\evil.com` and `\\evil.com` are evaluated as the `//`-prefixed paths
  // they would actually resolve to.
  const normalized = trimmed.replace(/\\/g, '/');

  // Require a single-slash-prefixed relative path that is not protocol-relative.
  if (!normalized.startsWith('/') || normalized.startsWith('//')) {
    return SAFE_REDIRECT_DEFAULT;
  }

  return normalized;
}

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = safeRedirect(searchParams.get('redirect'));
  const setAuth = useAuthStore((s) => s.setAuth);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: '', password: '' });
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [providers, setProviders] = useState<('google' | 'github')[]>([]);

  useEffect(() => {
    authService.oauthAvailableProviders().then(setProviders).catch(() => undefined);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = await authService.login(form);
      if ('requires2fa' in data) {
        setChallengeToken(data.challengeToken);
        setLoading(false);
        return;
      }
      setAuth(data.user, data.tokens.accessToken, data.tokens.refreshToken);
      toast.success('Giriş başarılı');
      navigate(redirectTo);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Giriş başarısız');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!challengeToken) return;
    setLoading(true);
    try {
      const data = await authService.loginVerify(challengeToken, code);
      setAuth(data.user, data.tokens.accessToken, data.tokens.refreshToken);
      toast.success('Giriş başarılı');
      navigate(redirectTo);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Doğrulama kodu hatalı');
    } finally {
      setLoading(false);
    }
  };

  if (challengeToken) {
    return (
      <div>
        <div className="mb-6 flex flex-col items-center gap-3">
          <div className="rounded-full bg-primary-100 p-3 dark:bg-primary-900/30">
            <ShieldCheck className="h-7 w-7 text-primary-600 dark:text-primary-400" />
          </div>
          <h2 className="text-center text-xl font-semibold text-gray-900 dark:text-white">
            İki Adımlı Doğrulama
          </h2>
          <p className="text-center text-sm text-gray-500 dark:text-gray-400">
            {recoveryMode
              ? 'Kurtarma kodunuzu girin (örn: 1a2b-3c4d).'
              : 'Authenticator uygulamanızdaki 6 haneli kodu girin.'}
          </p>
        </div>

        <form onSubmit={handleVerify} className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {recoveryMode ? 'Kurtarma Kodu' : 'Doğrulama Kodu'}
            </label>
            <div className="relative">
              <KeyRound className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                inputMode={recoveryMode ? 'text' : 'numeric'}
                autoComplete="one-time-code"
                required
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="w-full rounded-xl border border-gray-300 bg-gray-50 py-2.5 pl-10 pr-4 text-center font-mono text-base tracking-widest text-gray-900 placeholder-gray-400 transition-colors focus:border-primary-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700/50 dark:text-gray-100 dark:placeholder-gray-500"
                placeholder={recoveryMode ? '1a2b-3c4d' : '000000'}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="group flex w-full items-center justify-center gap-2 rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-primary-500/25 transition-all hover:bg-primary-700 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Doğrulanıyor…
              </>
            ) : (
              <>
                Doğrula ve Giriş Yap
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </>
            )}
          </button>
        </form>

        <div className="mt-6 space-y-2 text-center text-sm">
          <button
            type="button"
            onClick={() => {
              setRecoveryMode(!recoveryMode);
              setCode('');
            }}
            className="font-medium text-primary-600 transition-colors hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
          >
            {recoveryMode ? 'Authenticator kodunu kullan' : 'Kurtarma kodu kullan'}
          </button>
          <div>
            <button
              type="button"
              onClick={() => {
                setChallengeToken(null);
                setCode('');
                setRecoveryMode(false);
              }}
              className="text-gray-500 transition-colors hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            >
              Farklı hesap ile giriş yap
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="mb-1 text-center text-xl font-semibold text-gray-900 dark:text-white">
        Tekrar hoş geldiniz
      </h2>
      <p className="mb-6 text-center text-sm text-gray-500 dark:text-gray-400">
        Hesabınıza giriş yapın
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Email
          </label>
          <div className="relative">
            <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="ornek@email.com"
              className="w-full rounded-xl border border-gray-300 bg-gray-50 py-2.5 pl-10 pr-4 text-sm text-gray-900 placeholder-gray-400 transition-colors focus:border-primary-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700/50 dark:text-gray-100 dark:placeholder-gray-500 dark:focus:border-primary-400 dark:focus:bg-gray-700 dark:focus:ring-primary-400/20"
              data-testid="login-email-input"
            />
          </div>
        </div>

        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Şifre
            </label>
            <Link
              to="/forgot-password"
              className="text-xs font-medium text-primary-600 transition-colors hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
            >
              Şifremi unuttum
            </Link>
          </div>
          <div className="relative">
            <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
            <input
              type="password"
              required
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="••••••••"
              className="w-full rounded-xl border border-gray-300 bg-gray-50 py-2.5 pl-10 pr-4 text-sm text-gray-900 placeholder-gray-400 transition-colors focus:border-primary-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700/50 dark:text-gray-100 dark:placeholder-gray-500 dark:focus:border-primary-400 dark:focus:bg-gray-700 dark:focus:ring-primary-400/20"
              data-testid="login-password-input"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="group flex w-full items-center justify-center gap-2 rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-primary-500/25 transition-all hover:bg-primary-700 hover:shadow-lg hover:shadow-primary-500/30 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 dark:shadow-primary-400/10 dark:hover:shadow-primary-400/15"
          data-testid="login-submit"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Giriş yapılıyor…
            </>
          ) : (
            <>
              Giriş Yap
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </>
          )}
        </button>
      </form>

      {providers.length > 0 && (
        <>
          <div className="mt-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
            <span className="text-xs text-gray-400 dark:text-gray-500">veya</span>
            <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
          </div>
          <div className="mt-4">
            <OAuthButtons providers={providers} intent="login" />
          </div>
        </>
      )}

      <div className="mt-6 flex items-center gap-3">
        <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
        <span className="text-xs text-gray-400 dark:text-gray-500">veya</span>
        <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
      </div>

      <p className="mt-4 text-center text-sm text-gray-500 dark:text-gray-400">
        Hesabınız yok mu?{' '}
        <Link
          to="/register"
          className="font-semibold text-primary-600 transition-colors hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
        >
          Kayıt Ol
        </Link>
      </p>
    </div>
  );
}
